// WHO CAN FIX A BARRIER — the grouping Tier 3 is built on.
//
// V6 §E.6: barriers are grouped under three headings, and a flat ranked bar list
// is wrong because "it hides the grouping, and the grouping is the entire point —
// an owner needs to know which of these are theirs". The grouping is the panel; the
// counts are supporting detail.
//
// THE MAPPING FOLLOWS V6 §E.6 (user ruling 2026-09-01 — a deliberate reversal of
// the 2026-08-29 mapping, made because V6 is the authoritative superseding build
// spec):
//
//   property_decision → You can fix these   a decision the property owns — minimum
//                                            stay, pet policy, availability the
//                                            property controls, wanting a human
//   knowledge_base    → We can fix these     the agent's knowledge base should have
//   agent_config      → We can fix these     answered and did not, or the agent was
//                                            not configured to (e.g. quote a rate) —
//                                            both are our product's to correct
//   platform          → Nobody can           a technical failure — a dropped call,
//                                            bad audio, a website error
//   guest_side        → Nobody can           the guest changed their mind, or was
//                                            never going to book
//
// WHAT CHANGED FROM THE 2026-08-29 MAPPING, AND WHY. Two buckets moved:
// `knowledge_base` was "yours" and is now "ours" — V6 treats a gap the knowledge
// base should have covered as our product's to close, not the property's content to
// fix. `platform` was "ours" and is now "nobody" — a dropped call or bad audio is a
// technical failure nobody in this loop can act on, so it is not offered to the
// owner as theirs and not claimed by us as fixable. The other three are unchanged.
//
// IT FAILS LOUDLY ON AN UNKNOWN VALUE. A barrier whose `who_fixes` is not one of
// the five has nowhere to go, and the tempting behaviours — drop it, or bucket it
// under "Nobody can" — are both worse than a visible break: dropping loses a real
// barrier silently, and bucketing tells the owner that something they could fix is
// out of their hands. The backend gaining a sixth value must be a decision somebody
// makes, not a row that quietly stops appearing. `UNKNOWN_GROUP` is where that break
// surfaces, and the panel renders it under its own heading with the raw value
// visible. If the payload's who_fixes stops mapping cleanly to these five at
// runtime, that is a possible backend change to flag, not a fallback to paper over.

export const GROUPS = {
  YOURS: "You can fix these",
  OURS: "We can fix these",
  NOBODY: "Nobody can",
};

/** The heading order, which is also the reading order: yours first. */
export const GROUP_ORDER = [GROUPS.YOURS, GROUPS.OURS, GROUPS.NOBODY];

const MAPPING = {
  property_decision: GROUPS.YOURS,
  knowledge_base: GROUPS.OURS,
  agent_config: GROUPS.OURS,
  platform: GROUPS.NOBODY,
  guest_side: GROUPS.NOBODY,
};

/** The values the backend returns today, exported so tests cannot drift. */
export const KNOWN_WHO_FIXES = Object.keys(MAPPING);

/** Where an unrecognised value lands. Rendered, never dropped. */
export const UNKNOWN_GROUP = "Not classified";

/**
 * The heading a barrier belongs under.
 * @returns {string} one of GROUP_ORDER, or UNKNOWN_GROUP for anything else —
 *   including null, which is a backend that did not classify the barrier at all.
 */
export const groupFor = (whoFixes) => MAPPING[whoFixes] || UNKNOWN_GROUP;

/**
 * Group barriers for rendering.
 *
 * Returns groups in GROUP_ORDER, each with its rows in the order the backend
 * gave them — which is descending by count, and NOT re-sorted here. Empty groups
 * are omitted: a heading with nothing under it reads as a category the property
 * has no problems in, which is a claim the data does not make.
 *
 * Any unknown group is appended LAST and flagged, so it is visible without
 * displacing the three that were designed.
 *
 * @param {Array<{barrier?: string, label?: string, count?: number, who_fixes?: string, what_it_means?: string}>} rows
 * @returns {Array<{heading: string, rows: Array, unknown: boolean, total: number}>}
 */
export const groupBarriers = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const buckets = new Map();
  for (const row of list) {
    if (!row) continue;
    const heading = groupFor(row.who_fixes);
    if (!buckets.has(heading)) buckets.set(heading, []);
    buckets.get(heading).push(row);
  }
  const ordered = [];
  for (const heading of GROUP_ORDER) {
    const group = buckets.get(heading);
    if (group && group.length) {
      ordered.push({ heading, rows: group, unknown: false, total: group.length });
    }
  }
  const strays = buckets.get(UNKNOWN_GROUP);
  if (strays && strays.length) {
    ordered.push({ heading: UNKNOWN_GROUP, rows: strays, unknown: true, total: strays.length });
  }
  return ordered;
};

/**
 * How many barriers are the property's own to fix — the figure the glass note
 * above the panel carries. Null when there are none, so the note renders nothing
 * rather than "0 of these are yours to fix", which is a sentence nobody needs.
 */
export const yoursToFixCount = (rows) => {
  const n = (Array.isArray(rows) ? rows : [])
    .filter((r) => groupFor(r?.who_fixes) === GROUPS.YOURS)
    .reduce((sum, r) => sum + (Number(r?.count) || 0), 0);
  return n > 0 ? n : null;
};
